import type { Action, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { isAction, isFulfilled, isPending, isRejected, isRejectedWithValue } from '@reduxjs/toolkit';
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { EndpointBuilder, TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
    BaseQueryFn,
    createApi,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import axios, {
    AxiosError,
    AxiosHeaders,
    type AxiosResponse,
    type AxiosResponseHeaders,
    type InternalAxiosRequestConfig,
    type RawAxiosResponseHeaders,
} from 'axios';
import { type ActiveCreation, activeCreationOf, createEndpointOf } from 'components/create-dialog/activeCreation';
import { isSendingSectionRequest } from 'components/section-edit/saveSection';
import { disableButtons } from 'store/reducers/buttons';
import { triggerNotFound } from 'store/reducers/notFound';
import { answerTourRequest, isTourMode } from 'tour/tourMode';
import { isClosingRefusal } from 'types/caseClosing';
import { isNotSharedPaymentRefusal } from 'types/invoices';
import { CountDto } from 'types/count';
import { PaginationProps } from 'types/paginationAndFilter';
import { errorAlert, successAlert } from 'utils/alert';
import { captureApiRequestFailed, failureStatusOf } from 'utils/apiRequestAnalytics';
import { getIntl } from 'utils/getTranslations';
import { objectToQueryParams } from 'utils/linkUtil';
import { logError, logInfo } from 'utils/monitoring';
import {
    observePrivilegesVersionHeader,
    PRIVILEGES_VERSION_HEADER,
    privilegesVersionTracker,
} from 'utils/privilegesVersion';
import * as yup from 'yup';
import { handleRTKQueryError } from './util/errorHandling';

const UPLOAD_ENDPOINTS = [
    'sendInvoiceEmailMessage',
    'sendDebtInvoiceReminderMessage',
    'sendInvoiceMessage',
    'sendCoiCheckEmailMessage',
    'uploadBankTransfers',
    'uploadSentInvoice',
    'updateInvoiceLogo',
    'updateTenantIcon',
];

const getHeaders = async (
    headers: Headers,
    { endpoint }: Pick<BaseQueryApi, 'getState' | 'extra' | 'endpoint' | 'type' | 'forced'>
) => {
    if (!UPLOAD_ENDPOINTS.includes(endpoint)) {
        headers.set('Content-Type', 'application/json');
    }
    setAcceptHeader(headers);

    return headers;
};

const setAcceptHeader = (headers: Headers): void => {
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
};

const convertFetchApiHeadersToAxiosHeaders = (headers: Headers): Record<string, string> => {
    const obj: Record<string, string> = {};
    headers.forEach((value, key) => {
        obj[key] = value;
    });
    return obj;
};

const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL,
    prepareHeaders: getHeaders,
    credentials: 'include',
});

export const portalApiAxios = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
});

// An interceptor can only change a request; answering it from the tour's sample data takes an adapter.
const tourAdapter = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
    const answer = answerTourRequest(config.method ?? 'GET', config.url ?? '');
    if (!answer) {
        return axios.getAdapter(axios.defaults.adapter)(config);
    }
    if ('data' in answer) {
        return { data: answer.data, status: 200, statusText: 'OK', headers: {}, config };
    }
    // A refused write reads as forbidden to the caller.
    const response = { data: undefined, status: answer.error === 404 ? 404 : 403, statusText: '', headers: {}, config };
    throw new AxiosError(
        `Answered ${response.status} in tour mode`,
        AxiosError.ERR_BAD_REQUEST,
        config,
        null,
        response
    );
};

portalApiAxios.interceptors.request.use(async (config) => {
    const headers: Headers = new Headers();
    setAcceptHeader(headers);
    config.headers = new AxiosHeaders({ ...config.headers.toJSON(), ...convertFetchApiHeadersToAxiosHeaders(headers) });
    if (isTourMode()) {
        config.adapter = tourAdapter;
    }
    return config;
});

const observeAxiosPrivilegesVersion = (headers: RawAxiosResponseHeaders | AxiosResponseHeaders | undefined) => {
    // Axios lower-cases response header names
    const version = headers?.[PRIVILEGES_VERSION_HEADER.toLowerCase()];
    privilegesVersionTracker.observe(typeof version === 'string' ? version : null);
};

portalApiAxios.interceptors.response.use(
    (response) => {
        observeAxiosPrivilegesVersion(response.headers);
        return response;
    },
    (error: unknown) => {
        // A 403 is often the first sign of a revocation, and axios routes it here
        if (axios.isAxiosError(error)) observeAxiosPrivilegesVersion(error.response?.headers);
        return Promise.reject(error);
    }
);

export interface CustomExtraOptions extends Record<string, unknown> {
    skipButtonDisable?: boolean;
}

export interface ExtendedFetchArgs extends FetchArgs {
    _skipButtonDisable?: boolean;
}

const customBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    CustomExtraOptions,
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    const reqMethod = typeof args === 'object' ? args.method : '';
    const tourAnswer = answerTourRequest(reqMethod || 'GET', typeof args === 'string' ? args : args.url);
    if (tourAnswer) {
        if ('data' in tourAnswer) return { data: tourAnswer.data };
        return {
            error:
                tourAnswer.error === 404
                    ? { status: 404, data: undefined }
                    : { status: 'CUSTOM_ERROR', error: 'Refused in tour mode' },
        };
    }
    const skipFromArgs = typeof args === 'object' ? (args as ExtendedFetchArgs)._skipButtonDisable : false;
    const shouldDisableButtons =
        (reqMethod === 'POST' || reqMethod === 'PUT' || reqMethod === 'PATCH' || reqMethod === 'DELETE') &&
        !extraOptions?.skipButtonDisable &&
        !skipFromArgs;

    try {
        // Disable the submit buttons
        if (shouldDisableButtons) {
            api.dispatch(disableButtons(true));
        }

        const result = await baseQuery(args, api, extraOptions);
        observePrivilegesVersionHeader(result.meta?.response?.headers);
        if (result.error) {
            // Skip logging for expected auth errors (401/403)
            if (result.error.status !== 401 && result.error.status !== 403) {
                console.error('API error:', {
                    url: typeof args === 'string' ? args : args.url,
                    method: reqMethod,
                    status: result.error.status,
                    data: result.error.data,
                    error: result.error,
                });
                handleRTKQueryError(result, 'customBaseQuery', args);
            }

            if (result.error.status === 401) {
                logout();
                window.location.href = '/login';
            }
        }
        return result;
    } catch (exception) {
        console.error('[customBaseQuery] Exception caught:', {
            url: typeof args === 'string' ? args : args.url,
            method: reqMethod,
            exception,
            args,
            extraOptions,
        });

        handleRTKQueryError(exception, 'customBaseQuery', args);
        throw exception;
    } finally {
        // Enable the save buttons again
        if (shouldDisableButtons) {
            api.dispatch(disableButtons(false));
        }
    }
};

// Define a service using a base URL and expected endpoints
export const portalAPI = createApi({
    reducerPath: 'portalAPI',
    baseQuery: customBaseQuery,
    endpoints: () => ({}),
});

export const injectCountEndPoint = <T extends string>(
    builder: EndpointBuilder<
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        string,
        'portalAPI'
    >,
    basePath: string,
    tagType: TagDescription<T>,
    keepUnusedDataFor?: number | undefined
) => {
    return builder.query<CountDto, Pick<PaginationProps, 'filters' | 'extraParams'>>({
        query: ({ filters, extraParams }) =>
            `${basePath}/count${objectToQueryParams({ filter: filters ?? '', ...extraParams })}`,
        providesTags: [tagType],
        keepUnusedDataFor: keepUnusedDataFor === undefined ? 5 * 60 : keepUnusedDataFor,
    });
};

const logout = async () => {
    localStorage.removeItem('cognito_user');
};

export const ERROR_CODE_MESSAGES: Map<string, string | null> = new Map(
    Object.entries({
        MESSAGE_BUILD_FAILURE: 'email-error.message-build-failure',
        WHITELIST_ERROR: 'email-error.whitelist',
        EMAIL_TRANSPORT_FAILURE: 'email-error.transport-failure',
        CLOSED_ASSIGNMENT_UNINVOICED_AMOUNT: 'biz-error.closed-assignment-uninvoiced-amount',
        CLOSED_CASE_UNINVOICED_AMOUNT: 'biz-error.closed-case-uninvoiced-amount',
        CLOSED_CASE_UNINVOICED_EXPENSES: 'biz-error.closed-case-uninvoiced-expenses',
        CLOSED_CASE_UNFINISHED_TASKS: 'biz-error.closed-case-unfinished-tasks',
        CLOSED_CASE_UNSETTLED_INVOICES: 'biz-error.closed-case-unsettled-invoices',
        CLOSED_CASE_UNUSED_PREPAYMENTS: 'biz-error.closed-case-unused-prepayments',
        CLOSED_CASE_CONFIDENTIAL_REASON_REQUIRED: 'biz-error.closed-case-confidential-reason-required',
        CLOSED_CASE_CONFIDENTIALITY_DECISION_REQUIRED: 'biz-error.closed-case-confidentiality-decision-required',
        CLOSE_ON_SEND_NOT_ALLOWED: 'biz-error.close-on-send-not-allowed',
        INVOICE_SPEC_TEMPLATE_NOT_BOUND: 'biz-error.invoice-spec-not-bound',
        INVOICE_TEMPLATE_NOT_BOUND: 'biz-error.invoice-not-bound',
        JOB_ROLE_EMPLOYMENT_FOUND: 'biz-error.job-role-employment-found',
        JOB_ROLE_PRICE_RATE_FOUND: 'biz-error.job-role-price-rate-found',
        INVOICE_MATTER_INVALID_STATE: 'biz-error.invoice-matter-invalid-state',
        INVOICE_MATTER_OPEN_INVOICES: 'biz-error.invoice-matter-open-invoices',
        FX_RATE_NOT_FOUND: 'biz-error.fx-rate-not-found',
        CONTRACT_TEMPLATE_NOT_BOUND: 'biz-error.contract-template-not-bound',
        CONTRACT_CONNECTED_TO_CASE: 'biz-error.contract-connected-to-case',
        MALFORMED_TEMPLATE: 'biz-error.malformed-template',
        MISSING_CONTENT: 'biz-error.missing-content',
        ENTITY_PERSISTENCE_ERROR: 'biz-error.entity-persistence-error',
        TOO_MANY_ACTIVE_EXPORTS: 'biz-error.too-many-active-exports',
        EXPORT_LIMIT_EXCEEDED: 'biz-error.export-limit-exceeded',
        SMAILY_SYNC_LIMIT_EXCEEDED: 'biz-error.smaily-sync-limit-exceeded',
        NOT_FOUND: 'app-error.not-found',
    })
);

const API_ERROR_MESSAGES: Map<string, string | null> = new Map(
    Object.entries({
        getCorporateRoleFillingsWithFilters: 'corporate-role.error.fetching',
        updateCorporateRoleFillings: 'corporate-role.error.updating',
        getCompanyById: 'companies.error-fetching-company',
        getCompaniesList: 'companies.error-loading-companies-list',
        getContactById: 'contacts.error-fetching-contact',
        getContactsList: 'contacts.error-loading-contacts-list',
        addContact: 'contacts.error-creating-contact',
        updateContact: 'contacts.error-updating-contact',
        deleteContact: 'contacts.error-deleting-contact',
        mergeContacts: 'contacts.merge.error',
        addCoiCheck: 'coi.error-creating-coi-check',
        getEffortsList: 'efforts.error.loading-list',
        getEffortById: 'efforts.error.fetching',
        addEffort: 'efforts.error.creating',
        updateEffort: 'efforts.error.updating',
        updateNominationStatus: 'rankings-referees.status-update-error',
        deleteEffort: 'efforts.error.deleting',
        getGroupsWithFilters: 'groups.error.loading-list',
        getPartyGroupById: 'groups.error.fetching',
        addPartyGroup: 'groups.error.creating',
        updatePartyGroup: 'groups.error.updating',
        deletePartyGroup: 'groups.error.deleting',
        getExpenseById: 'expenses.error.fetching',
        getExpensesList: 'expenses.error.loading-list',
        addExpense: 'expenses.error.creating',
        updateExpense: 'expenses.error.updating',
        deleteExpense: 'expenses.error.deleting',
        getPracticeAreaById: 'practice-areas.error-fetching-practice-area',
        getPracticeAreasList: 'practice-areas.error-loading-practice-areas-list',
        addPracticeArea: 'practice-areas.error-creating-practice-area',
        updatePracticeArea: 'practice-areas.error-updating-practice-area',
        deletePracticeArea: 'practice-areas.error-deleting-practice-area',
        getTeamById: 'teams.error-fetching-team',
        getTeamsList: 'teams.error-loading-teams-list',
        addTeam: 'teams.error-creating-team',
        updateTeam: 'teams.error-updating-team',
        deleteTeam: 'teams.error-deleting-team',
        getEmployeeById: 'employees.error-fetching-employee',
        getEmployeesList: 'employees.error-loading-employees-list',
        addEmployee: 'employees.error-creating-employee',
        updateEmployee: 'employees.error-updating-employee',
        deleteEmployee: 'employees.error-deleting-employee',
        getEmployeeWeb: 'employees.web.error-fetching',
        updateEmployeeWeb: 'employees.web.error-updating',
        updateEmployeeWebPicture: 'employees.web.error-updating-picture',
        deleteEmployeeWebPicture: 'employees.web.error-removing-picture',
        getOfficeById: 'offices.error-fetching-office',
        getOfficesList: 'offices.error-loading-offices-list',
        addOffice: 'offices.error-creating-office',
        updateOffice: 'offices.error-updating-office',
        deleteOffice: 'offices.error-deleting-office',
        getContractById: 'contracts.error-fetching-contract',
        getContractsList: 'contracts.error-loading-contracts-list',
        getContractsListByPartyId: 'contracts.error-loading-contracts-list',
        addContract: 'contracts.error-creating-contract',
        updateContract: 'contracts.error-updating-contract',
        deleteContract: 'contracts.error-deleting-contract',
        getClassifierByCode: 'classifiers.error-fetching-classifier',
        getClassifiersList: 'classifiers.error-loading-classifiers-list',
        getJobRoleById: 'job-roles.error-fetching-job-role',
        getJobRolesList: 'job-roles.error-fetching-job-roles-list',
        addJobRole: 'job-roles.error-creating-job-role',
        updateJobRole: 'job-roles.error-updating-job-role',
        deleteJobRole: 'job-roles.error-deleting-job-role',
        getFinancialConditionById: 'financial-conditions.error-fetching-financial-condition',
        getFinancialConditionsList: 'financial-conditions.error-loading-financial-conditions-list',
        addFinancialCondition: 'financial-conditions.error-creating-financial-condition',
        updateFinancialCondition: 'financial-conditions.error-updating-financial-condition',
        deleteFinancialCondition: 'financial-conditions.error-deleting-financial-condition',
        getMatterById: 'matters.error-fetching-matter',
        getInvoiceById: 'invoices.error-fetching-invoice',
        getMattersList: 'matters.error-loading-matters-list',
        getMattersListByPartyId: 'matters.error-loading-matters-list',
        addMatter: 'matters.error-creating-matter',
        updateMatter: 'matters.error-updating-matter',
        deleteMatter: 'matters.error-deleting-matter',
        addTask: 'tasks.error-creating-task',
        addMatterInsider: 'matters.insider-list.errors.creating',
        updateMatterInsider: 'matters.insider-list.errors.updating',
        refreshMatterInsider: 'matters.insider-list.errors.updating',
        getMatterDeleteValidation: null,
        getMatterAssignmentDeleteValidation: null,
        getPricelistById: 'pricelists.error-fetching-pricelist',
        getPricelistsList: 'pricelists.error-loading-pricelists-list',
        addPricelist: 'pricelists.error-creating-pricelist',
        updatePricelist: 'pricelists.error-updating-pricelist',
        updateMatterPricelist: 'pricelists.error-updating-pricelist',
        deletePricelist: 'pricelists.error-deleting-pricelist',
        addTimecard: 'timecards.error-creating-timecard',
        finishTimecard: 'timecards.error-finishing-timecard',
        // The description's improvement toasts its own failures.
        improveTimecardDescription: null,
        // The rules dialog shows the failure in its body, with Retry.
        getTimecardDescriptionRules: null,
        startTrackingTimecard: 'timecards.error-starting-tracking',
        pauseTrackingTimecard: 'timecards.error-pausing-tracking',
        getTimecardById: 'timecards.error-loading-timecard',
        // Sidebar tracker's speculative fetch of the stored tracked id: a 404 is expected when that
        // timecard is deleted, and the hook recovers locally — so no toast (and, by omission from
        // ENTITY_FETCH_OUTCOME, no /404 redirect).
        getTrackedTimecardById: null,
        getTimecardsExportList: 'timecards.error-loading-timecard-export-list',
        // Re-read whenever the tab becomes visible; a failed background sync stays silent, like the
        // header pollers below.
        getCurrentlyTrackedTimecard: null,
        updateTimecard: 'timecards.error-updating-timecard',
        deleteTimecard: 'timecards.error-deleting-timecard',
        findMatterRates: null,
        findPricelistRates: null,
        createDraftInvoices: null,
        createJointInvoice: null,
        createPrepaidInvoice: 'invoices.error-creating-prepaid-invoice',
        updateInvoice: 'invoices.error-updating-invoice',
        createInvoicePayment: 'invoices.error-adding-invoice-payment',
        deleteInvoicePayment: 'invoices.error-deleting-invoice-payment',
        sendInvoiceEmailMessage: 'email-sending-result.error-sending',
        sendDebtInvoiceReminderMessage: 'email-sending-result.error-sending',
        sendCoiCheckEmailMessage: 'email-sending-result.error-sending',
        uploadBankTransfers: 'bank-transfers.upload-bank-transfers-error',
        updateBankTransferInvoicePayments: 'bank-transfers.update-error',
        addProject: 'projects.actions.create-error',
        updateProject: 'projects.actions.update-error',
        deleteProject: 'projects.actions.delete-error',
        removeMatterFromProject: 'projects.actions.remove-matter-error',
        getUserData: null,
        // The configuration page toasts a failed setting change itself, with the switch flipped back.
        setBackOfficeSettingEnabled: null,
        setBackOfficeSettingScope: null,
        setBackOfficeSettingOption: null,
        // Header pollers and the editing-session heartbeat refresh data that is already on screen.
        // A browser resuming from sleep fires every throttled poll at once, before the network is
        // back, and one toast per failed poll is the result — so these stay silent and the next
        // poll simply picks up again.
        getNotificationsList: null,
        getTasksAddressedToEmployee: null,
        getActiveMaintenanceNotifications: null,
        sendEditingHeartbeat: null,
        // The navigation draws the tenant's initial when its logo or icon does not load.
        getCurrentTenantLogo: null,
        getCurrentTenantIcon: null,
        // Trainings alert on failure at the call site (topic/subtopic in use, form error), so the
        // global handler must stay silent here or every failure shows two toasts.
        addTraining: null,
        updateTraining: null,
        deleteTraining: null,
        addTrainingTopic: null,
        addTrainingSubtopic: null,
        setTrainingTopicHasSubtopics: null,
        deleteTrainingTopic: null,
        deleteTrainingSubtopic: null,
        // Same for debriefings: the form surfaces the backend's own validation message, and delete
        // renders the blocked-by-open-tasks modal instead of a toast.
        addDebriefing: null,
        updateDebriefing: null,
        deleteDebriefing: null,
        isOneDriveIntegrationEnabled: null,
        isNetvisorIntegrationEnabled: null,
        isWintimeIntegrationEnabled: null,
        isMaventaIntegrationEnabled: null,
        isSmailyIntegrationEnabled: null,
        sendToNetvisor: null,
        sendToInvoicing: null,
        retryProcessExecutionItem: 'processes.error-general',
        updateCurrentUserPreferences: 'user.error-updating-preferences',
        saveCurrentUserPreferencesQuietly: 'user.error-updating-preferences',
        getExportTasks: null,
        startMarketingProfileExport: null,
        triggerMarketingProfileSync: null,
        getMarketingProfileSyncOutcome: null,
        createMarketingProfile: null,
        updateMarketingProfile: null,
        deleteMarketingProfile: null,
        getHelpCenterIndex: null,
        getHelpCenterPage: null,
        searchHelpCenter: null,
        // The search surfaces draw their own empty/error states, and the bar re-queries on the next
        // keystroke — one toast per debounce is what a dropped connection would otherwise produce.
        globalSearch: null,
        globalSearchPeek: null,
        completeUiContextRequest: null,
        // The brief view owns its own failure states: a not-found message for a 404 and a
        // generic error state for everything else. A toast on top would repeat both.
        getMeetingPrepView: null,
        // Home shows its own failed state, with Retry, in place of its grid.
        getHome: null,
        saveHomeLayout: 'home.widgets.error.saving',
        getHomeWidgets: 'home.widgets.error.loading-widgets',
        createHomeWidget: 'home.widgets.error.creating',
        updateHomeWidget: 'home.widgets.error.updating',
        deleteHomeWidget: 'home.widgets.error.deleting',
        // A widget says in its own card that its figure failed or is closed to the viewer. Without the
        // catalogue it only loses its links.
        getWidgetCatalogue: null,
        getWidgetFigure: null,
        previewWidgetFigure: null,
        getSavedViewAnswer: null,
        // The gallery tab, the Firm widgets page and the share dialog each say in place that their list failed;
        // a head count that fails leaves its line out.
        getWidgetGallery: null,
        getFirmWidgets: null,
        countRecipients: null,
        setWidgetSharing: 'home.widgets.error.sharing',
    })
);

const API_SUCCESS_MESSAGES: Map<string, string | null> = new Map(
    Object.entries({
        updateImportationDate: 'configuration.success.updating',
        updateConversionRateConfiguration: 'configuration.success.updating',
        updateInvoiceAutoGeneration: 'configuration.success.updating',
        updateDebtCollection: 'configuration.success.updating',
        updateTimecardEntryControl: 'configuration.success.updating',
        updatePrimaryOfficeForCases: 'configuration.success.updating',
        updateDefaultHandledByEmployees: 'configuration.success.updating',
        updateHideInvoicesOlderThanDays: 'configuration.success.updating',
        updateDefaultAdminFeePercentage: 'configuration.success.updating',
        updateRankingsRefereeRestPeriod: 'configuration.success.updating',
        updateDraftInvoiceSupervisingPartnerNotification: 'configuration.success.updating',
        updateCaseClosingConfiguration: 'configuration.success.updating',
        updateClientAssetAccounts: 'configuration.success.updating',
        updateInvoiceLogo: 'configuration.success.updating',
        resetInvoiceLogo: 'configuration.success.updating',
        updateTenantIcon: 'configuration.success.updating',
        removeTenantIcon: 'configuration.success.updating',
        updateCorporateRoleFillings: 'corporate-role.success.updating',
        addContact: 'contacts.contact-created',
        updateContact: 'contacts.contact-updated',
        deleteContact: 'contacts.contact-deleted',
        addPartyAsset: 'client-assets.success.creating',
        updatePartyAsset: 'client-assets.success.updating',
        deletePartyAsset: 'client-assets.success.deleting',
        addExpense: 'expenses.success.creating',
        updateExpense: 'expenses.success.updating',
        deleteExpense: 'expenses.success.deleting',
        addEffort: 'efforts.success.creating',
        updateEffort: 'efforts.success.updating',
        deleteEffort: 'efforts.success.deleting',
        addPartyGroup: 'groups.success.creating',
        updatePartyGroup: 'groups.success.updating',
        deletePartyGroup: 'groups.success.deleting',
        addPracticeArea: 'practice-areas.practice-area-created',
        updatePracticeArea: 'practice-areas.practice-area-updated',
        deletePracticeArea: 'practice-areas.practice-area-deleted',
        addEmployee: 'employees.employee-created',
        updateEmployee: 'employees.employee-updated',
        deleteEmployee: 'employees.employee-deleted',
        updateEmployeeWeb: 'employees.web.updated',
        addOffice: 'offices.office-created',
        updateOffice: 'offices.office-updated',
        deleteOffice: 'offices.office-deleted',
        addTeam: 'teams.team-created',
        updateTeam: 'teams.team-updated',
        deleteTeam: 'teams.team-deleted',
        addJobRole: 'job-roles.job-role-created',
        updateJobRole: 'job-roles.job-role-updated',
        deleteJobRole: 'job-roles.job-role-deleted',
        addFinancialCondition: 'financial-conditions.financial-condition-created',
        updateCompanyBankAccounts: 'financial-conditions.company.bank-accounts-updated',
        updateFinancialCondition: 'financial-conditions.financial-condition-updated',
        deleteFinancialCondition: 'financial-conditions.financial-condition-deleted',
        addContract: 'contracts.contract-created',
        updateContract: 'contracts.contract-updated',
        deleteContract: 'contracts.contract-deleted',
        addMatter: 'matters.matter-created',
        updateMatter: 'matters.matter-updated',
        updateMatterCaseNumber: 'matters.case-number-saved',
        updateMatterSequenceNumber: 'matters.seq-nr-saved',
        deleteMatter: 'matters.matter-deleted',
        addMatterInsider: 'matters.insider-list.success.creating',
        updateMatterInsider: 'matters.insider-list.success.updating',
        refreshMatterInsider: 'matters.insider-list.success.refreshing',
        addPricelist: 'pricelists.pricelist-created',
        updatePricelist: 'pricelists.pricelist-updated',
        updateMatterPricelist: 'pricelists.pricelist-updated',
        deletePricelist: 'pricelists.pricelist-deleted',
        addTimecard: 'timecards.timecard-created',
        finishTimecard: 'timecards.timecard-finished',
        startTrackingTimecard: null,
        pauseTrackingTimecard: null,
        updateTimecardCache: null,
        findMatterRates: null,
        findPricelistRates: null,
        updateTimecard: 'timecards.timecard-updated',
        createDraftInvoices: null,
        createJointInvoice: null,
        createCreditInvoice: 'invoices.credit-invoice-created',
        createCorrectionInvoice: 'invoices.correction-invoice-created',
        createBlankCreditNote: 'invoices.credit-invoice-created',
        createPrepaidInvoice: 'invoices.invoice-prepaid-created',
        reissueDebitInvoice: 'invoices.debit-invoice-created',
        updateInvoice: 'invoices.invoice-updated',
        updateInvoiceLimited: 'invoices.invoice-updated',
        deleteInvoiceMatter: 'invoices.invoice-updated',
        mergeInvoice: 'invoices.invoice-merged',
        splitInvoice: 'invoices.invoice-copied',
        updateInvoiceBillingNotes: 'invoices.invoice-billing-notes-updated',
        updateInvoiceDebtNotes: 'invoices.invoice-debt-notes-updated',
        createInvoicePayment: 'invoices.invoice-payment-added',
        deleteInvoicePayment: 'invoices.invoice-payment-deleted',
        deleteTimecard: 'timecards.timecard-deleted',
        sendInvoiceEmailMessage: 'email-sending-result.successfully-sent',
        sendInvoiceMessage: 'email-sending-result.successfully-sent',
        sendDebtInvoiceReminderMessage: 'email-sending-result.successfully-sent',
        markAsSent: 'invoices.send-wo-email.success',
        sendCoiCheckEmailMessage: 'email-sending-result.successfully-sent',
        addSystemRole: 'roles-and-permissions.added-new-role',
        deleteSystemRole: 'roles-and-permissions.deleted-role',
        updateSystemRolePrivileges: 'roles-and-permissions.updated-role-permissions',
        addCorrespondence: 'correspondence.correspondence-created',
        updateCorrespondence: 'correspondence.correspondence-updated',
        uploadBankTransfers: 'bank-transfers.upload-bank-transfers-success',
        updateBankTransfer: 'bank-transfers.update-success',
        addProject: 'projects.actions.create-success',
        updateProject: 'projects.actions.update-success',
        deleteProject: 'projects.actions.delete-success',
        removeMatterFromProject: 'projects.actions.remove-matter-success',
        addEmployeeRoles: 'roles-and-permissions.employee-roles-added',
        removeEmployeeRoles: 'roles-and-permissions.employee-roles-removed',
        recalculateInvoiceDate: 'invoices.invoice-date-updated',
        updateCurrentUserPreferences: 'user.preferences-updated',
        createHomeWidget: 'home.widgets.success.created',
        updateHomeWidget: 'home.widgets.success.updated',
        deleteHomeWidget: 'home.widgets.success.deleted',
        setWidgetSharing: 'home.widgets.success.shared',
    })
);

/**
 * Create mutations with no message of their own: on a page the form tells of the new record itself, or leaves
 * for it and says nothing. The create dialog stays where it is and shows one toast for every creation, so each
 * has its message here, for a creation that ran from the dialog only.
 */
const CREATE_DIALOG_SUCCESS_MESSAGES: Map<string, string> = new Map(
    Object.entries({
        addTask: 'tasks.task-created',
        addExperienceRecord: 'experience-records.actions.create-success',
        addJobPosition: 'job-positions.job-position-created',
        addDebriefing: 'debriefing.form.saved',
        addTraining: 'trainings.modal.saved',
        createMarketingProfile: 'marketing-profiles.profile-saved',
        finishMultiAuthorTimecards: 'timecards.timecard-finished',
        addInvoiceTimecard: 'timecards.timecard-created',
        addMultiAuthorInvoiceTimecards: 'timecards.timecard-created',
    })
);

/**
 * The creation under way in the create dialog as each mutation in flight started. The mutation's toast goes by
 * it, not by the dialog as the answer arrives: the user may have minimised or closed the dialog meanwhile, and
 * the record that was saved is still that creation's.
 */
const creationAtStart = new Map<unknown, ActiveCreation | null>();

/** The creation the mutation started under; the one under way now for a mutation whose start was not seen. */
function takeCreation(requestId: unknown, endpointName: string): ActiveCreation | null {
    const atStart = creationAtStart.get(requestId);
    creationAtStart.delete(requestId);
    return atStart === undefined ? activeCreationOf(endpointName) : atStart;
}

/**
 * The success toast of a mutation. One that saved the record of a creation in the create dialog also offers to
 * open that record.
 */
function showSuccess(endpointName: string, creation: ActiveCreation | null) {
    const messageId =
        API_SUCCESS_MESSAGES.get(endpointName) || (creation && CREATE_DIALOG_SUCCESS_MESSAGES.get(endpointName));

    if (!messageId) {
        return;
    }
    const message = getIntl().formatMessage({ id: messageId });

    if (creation?.open) {
        successAlert(message, {
            action: { label: getIntl().formatMessage({ id: 'create-dialog.toast.open' }), onClick: creation.open },
        });
    } else {
        successAlert(message);
    }
}

interface ErrorAttributes {
    timestamp: Date;
    message: string;
    type: string;
    code: string;
    requestId: string;
}

interface ActionWithData extends Action {
    payload: { data?: { requestId?: string } } & Record<string, unknown>;
    meta: { arg: { endpointName: string; originalArgs?: unknown } } & Record<string, unknown>;
}

const errorAttributesSchema: yup.Schema<ErrorAttributes> = yup.object({
    timestamp: yup.date().required(),
    message: yup.string().required(),
    type: yup.string().required(),
    code: yup.string().required(),
    requestId: yup.string().required(),
});

export function errorMessage(payloadData?: unknown, endpointName?: string) {
    if (payloadData) {
        try {
            const errorAttributes = errorAttributesSchema.validateSync(
                typeof payloadData === 'string' ? JSON.parse(payloadData) : payloadData
            );
            const { code, message } = errorAttributes;

            if (code) {
                const codeErrorId = ERROR_CODE_MESSAGES.get(code);
                if (codeErrorId) {
                    return getIntl().formatMessage({
                        id: `error-code-message.${codeErrorId}`,
                    });
                }
            }

            return message;
        } catch (err) {
            logError(err, { payloadData, endpointName });
            console.error('[errorMessage] Failed to parse payload data:', err);
        }
    }

    if (!endpointName) {
        return 'technical-error';
    }

    return getIntl().formatMessage({
        id: API_ERROR_MESSAGES.get(endpointName) || 'technical-error',
    });
}

// Endpoint -> outcome resolver. For a rejected query against one of these endpoints, the
// resolver decides what the global error handler should do based on the HTTP status:
//   - 'redirect': dispatch /404 navigation
//   - 'suppress': do nothing (a wrapper hook owns the recovery, e.g. retrying via restricted-info)
//   - 'toast':    fall through to the normal API_ERROR_MESSAGES toast
// Anything that isn't 403/404 (5xx, network, etc.) is a real server failure, not a missing
// resource, so it falls through to the per-endpoint toast instead of masking as /404.
type EntityFetchOutcome = 'redirect' | 'suppress' | 'toast';
const missingOrForbidden = (status: number | string | undefined): EntityFetchOutcome =>
    status === 404 || status === 403 ? 'redirect' : 'toast';

const ENTITY_FETCH_OUTCOME: Record<string, (status: number | string | undefined) => EntityFetchOutcome> = {
    // Matter and invoice primaries use useXxxWithRestricted to retry on 403; suppress the
    // 403 toast so the wrapper can render the restricted view (or, if restricted-info also
    // fails, the restricted-info endpoint triggers the /404).
    getMatterById: (status) => (status === 404 ? 'redirect' : status === 403 ? 'suppress' : 'toast'),
    getInvoiceById: (status) => (status === 404 ? 'redirect' : status === 403 ? 'suppress' : 'toast'),
    getMatterRestrictedInfo: missingOrForbidden,
    getInvoiceRestrictedInfo: missingOrForbidden,
    getContactById: missingOrForbidden,
    getExpenseById: missingOrForbidden,
    getTimecardById: missingOrForbidden,
    getPartyGroupById: missingOrForbidden,
    getContractById: missingOrForbidden,
    getProjectById: missingOrForbidden,
    getCorrespondenceById: missingOrForbidden,
    getExperienceRecordById: missingOrForbidden,
    getCoiCheckSummaryById: missingOrForbidden,
    getBankTransferById: missingOrForbidden,
    getMarketingProfileById: missingOrForbidden,
    getTaskById: missingOrForbidden,
    getEmployeeById: missingOrForbidden,
    getEffortById: missingOrForbidden,
    getJobPositionById: missingOrForbidden,
    getJobRoleById: missingOrForbidden,
    getOfficeById: missingOrForbidden,
    getPracticeAreaById: missingOrForbidden,
    getTeamById: missingOrForbidden,
    getTrainingById: missingOrForbidden,
    // The create form's read of a picked past training: one deleted since the picker listed it is
    // un-picked by the form, which stays open. Any other failure toasts.
    getPastTrainingById: (status) => (status === 404 ? 'suppress' : 'toast'),
    getDebriefingById: missingOrForbidden,
    getPartyAssetById: missingOrForbidden,
};

// Endpoint -> whether the screen that sent the request explains this refusal itself, so a toast would only repeat it.
const REFUSAL_SHOWN_BY_CALLER: Record<string, (error: unknown) => boolean> = {
    // The Close case modal reloads its closing check and shows what now blocks the close.
    closeCase: isClosingRefusal,
    // A copy deleted elsewhere can leave the group a single invoice while its dialog is open; the dialog closes.
    getPaymentDistribution: isNotSharedPaymentRefusal,
    updatePaymentDistribution: isNotSharedPaymentRefusal,
};

// The requests, by id, whose failure the screen that sent them draws itself.
const failuresDrawnByCaller = new Set<string>();

/** Says the screen that sent this request draws its failure itself: the error handler raises no toast for it. */
export const drawsOwnFailure = (requestId: string): void => {
    failuresDrawnByCaller.add(requestId);
};

type SentQuery = { endpointName: string; originalArgs?: unknown };

// The reads a mounted page draws the failure of itself, for as long as it says so.
const loadsDrawnByPage = new Set<SentQuery>();

/**
 * The same for a read a query hook sends, which hands its caller no request id before the request can fail: while
 * registered, a failure of that endpoint for that argument raises no toast. Returns what ends it.
 */
export const drawsOwnLoadFailure = (endpointName: string, originalArgs: unknown): (() => void) => {
    const load = { endpointName, originalArgs };
    loadsDrawnByPage.add(load);
    return () => {
        loadsDrawnByPage.delete(load);
    };
};

const isLoadDrawnByPage = (sent: SentQuery): boolean =>
    [...loadsDrawnByPage].some(
        (load) => load.endpointName === sent.endpointName && load.originalArgs === sent.originalArgs
    );

// Monotonic counter — gives the notFound slice a fresh triggerKey on every dispatch so
// two failures in the same millisecond still produce distinct keys.
let notFoundCounter = 0;

export const rtkQueryErrorHandler: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
    // A section's save has no request id to name, so its requests are taken as they start.
    if (isPending(action) && isSendingSectionRequest()) {
        drawsOwnFailure(action.meta.requestId);
    }
    // A request settles once, so its entry goes whatever the outcome.
    const isDrawnByCaller =
        (isFulfilled(action) || isRejected(action)) && failuresDrawnByCaller.delete(action.meta.requestId);

    // In tour mode a refused write or a GET with no sample response neither toasts nor redirects.
    if (isTourMode()) {
        return next(action);
    }

    const getErrorMessage = () => {
        if (!isAction(action)) {
            return;
        }
        const payloadData = (action as ActionWithData).payload?.data;
        const endpointName = (action as ActionWithData).meta?.arg?.endpointName;
        return errorMessage(payloadData, endpointName);
    };

    if (isAction(action) && action.type === 'portalAPI/executeMutation/pending') {
        const { arg, requestId } = (action as ActionWithData).meta;
        creationAtStart.set(requestId, activeCreationOf(createEndpointOf(arg.endpointName, arg.originalArgs)));
    } else if (isAction(action) && action.type === 'portalAPI/executeMutation/rejected') {
        creationAtStart.delete((action as ActionWithData).meta.requestId);
    }

    if (isFulfilled(action) && action.type === 'portalAPI/executeMutation/fulfilled') {
        const { arg, requestId } = (action as ActionWithData).meta;
        const endpointName = createEndpointOf(arg.endpointName, arg.originalArgs);
        showSuccess(endpointName, takeCreation(requestId, endpointName));
    } else if (
        isRejectedWithValue(action) &&
        (action.type === 'portalAPI/executeMutation/rejected' || action.type === 'portalAPI/executeQuery/rejected')
    ) {
        const endpointName = (action as ActionWithData).meta.arg.endpointName;
        const status = (action as ActionWithData).payload?.status as number | string | undefined;
        captureApiRequestFailed({
            endpoint: endpointName,
            kind: action.type === 'portalAPI/executeMutation/rejected' ? 'mutation' : 'query',
            status: failureStatusOf((action as ActionWithData).payload),
        });
        const outcome: EntityFetchOutcome | undefined = ENTITY_FETCH_OUTCOME[endpointName]?.(status);
        // customBaseQuery has already dropped the login state and is navigating to /login on a 401,
        // so a toast would only flash over that redirect — once for every request a resumed tab
        // fires at a session that lapsed meanwhile.
        const sessionLapsed = status === 401;
        const isShownByCaller =
            isDrawnByCaller ||
            isLoadDrawnByPage((action as ActionWithData).meta.arg) ||
            (REFUSAL_SHOWN_BY_CALLER[endpointName]?.(action.payload) ?? false);

        if (outcome === 'redirect') {
            // Info log so ops still see the access/missing signal even though
            // the user is silently bounced to /404 (the page intentionally conflates
            // "deleted", "never existed", and "no access").
            const payloadData = (action as ActionWithData)?.payload?.data;
            logInfo(`${endpointName} -> /404 (status ${status ?? 'unknown'})`, {
                category: 'not-found',
                endpointName,
                status,
                requestId: payloadData?.requestId,
            });
            api.dispatch(triggerNotFound(`${endpointName}:${++notFoundCounter}`));
        } else if (
            !sessionLapsed &&
            !isShownByCaller &&
            outcome !== 'suppress' &&
            API_ERROR_MESSAGES.get(endpointName) !== null
        ) {
            const payloadData = (action as ActionWithData)?.payload?.data;
            errorAlert(getErrorMessage() || '', { requestId: payloadData?.requestId });
        }
    }

    return next(action);
};
